import React, {useCallback, useEffect, useState} from 'react';
import {Col, Container, Row} from 'reactstrap';
import classNames from 'classnames';
import {Location} from '@reach/router';
import queryString from 'query-string';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCalendarCheck, faClock, faShareSquare} from '@fortawesome/pro-light-svg-icons';
import {faArrowDown} from '@fortawesome/pro-regular-svg-icons';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import useBalanceText from 'utils/useBalanceText';

import PageMeta from 'components/PageMeta';
import Header from 'components/Elements/Header';
import Section, {SectionImage, SectionTriangle, SectionTriangleSpeakerCollapse} from 'components/Elements/Section';
import CountdownTimer from 'components/Elements/CountdownTimer';
import Video from 'components/Video';
import IconList, {IconListItem, ListIcon} from 'components/Elements/IconList';
import OptinModal, {OptinModalTrigger} from 'components/OptinModal';
import ModalExitLanding from 'components/Modals/ModalExitLanding';
import ShareButtons from 'components/Social/ShareButtons';
import Footer from 'components/Elements/Footer';
import {TestimonialBubble} from 'components/Testimonial';

export default function Index(props) {
  return (
    <Location>
      {({location, navigate}) => <MasterClassIndex location={location} navigate={navigate} {...props} />}
    </Location>
  );
}
function MasterClassIndex({
  location = {},
  noShare = false,
  noFixedVideo = false,
  noExitIntent = false,
  slug = 'masterclass',
  navigate,
  variant = 'default',
  ...props
}) {
  const qs = typeof window !== 'undefined' ? queryString.parse(window.location.search) : {};
  const navigateWatch = useCallback(() => navigate('/masterclass/watch/'), []);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const pathName = window.location.pathname;
      const pathRegex = new RegExp(`\/${slug}\/`, 'g');
      if (!pathName.match(pathRegex)) {
        navigate(`/${slug}/` + location.search);
      }
    }
  }, []);

  const [ctaButtonTextControl, setCtaButtonTextControl] = useState(props.ctaButtonTextControl || 'control');
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setCtaButtonTextControl('v1');
          break;
        case 2:
          setCtaButtonTextControl('v2');
          break;
      }
    }, 1135);
  }, []);

  const [designControl, setDesignControl] = useState(props.designControl || 'control');
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setDesignControl('v1');
          break;
      }
    }, 902);
  }, []);

  const [headlineControl, setheadlineControl] = useState(props.headlineControl || 'control');
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setheadlineControl('v1');
          break;
        case 2:
          setheadlineControl('v2');
          break;
      }
    }, 1136);
  }, []);

  const videoUrl = 'https://www.youtube.com/watch?v=OD4CXU-7MYE';
  const videoThumbUrl = 'https://cdn.foodrevolution.org/tt2/videothumb/tt2-landing-dynamicthumbnail-20230626-1.gif';
  const videoButton = true;

  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchStart: '',
        launchEnd: '',
        launchTitle: ''
      }
    }
  } = usePhaseState();

  const launchEnd = new Date(defaultSchedule.launchEnd).getTime();

  function LandingTimer({className = null, ...props}) {
    return (
      <div className={classNames(`section-countdown countdown-${props.light ? 'light' : 'dark'}`, className)}>
        <h5 className="timer-heading mt-0 mb-2">{defaultSchedule.launchTitle} ends in:</h5>
        <CountdownTimer time={launchEnd} />
      </div>
    );
  }

  function ModalTrigger({className = 'mb-4'}) {
    return (
      <OptinModalTrigger className={className}>
        <>
          {
            {
              control: (
                <>
                  Reserve Your <i>Free</i> Spot Now
                </>
              ),
              v1: (
                <>
                  Reserve My <i>Free</i> Spot Now
                </>
              ),
              v2: <>Reserve My Spot</>
            }[ctaButtonTextControl]
          }
        </>
      </OptinModalTrigger>
    );
  }

  function Logo({className = null, src = 'https://cdn.foodrevolution.org/tt2/tt2m-logo-2024.svg'}) {
    return <img className={className} src={src} alt="Tackling Type 2 Masterclass logo" style={{maxHeight: '100px'}} />;
  }

  useBalanceText();
  return (
    <div className="page d-flex flex-column">
      <PageMeta />
      <div className="page-content">
        {
          {
            control: (
              <>
                <Header bgColor="purple" lockupWhite adLockup={variant === 'ad' ? true : false} />

                <Section
                  id="landing-header-cta"
                  color="black"
                  className="section-angle-first text-center text-white"
                  angle="none"
                >
                  <Container>
                    <Row className="justify-content-end">
                      <Col lg="7">
                        {variant === 'ad' ? (
                          <>
                            {
                              {
                                control: (
                                  <>
                                    <p>FREE Masterclass Now Playing For A Limited Time!</p>
                                    <h4 className="section-heading mb-3">
                                      <>Conventional Wisdom Says Diabetics Need To Cut Out Sugar.</>
                                    </h4>
                                    <h3 className="mt-0 mb-3">Here’s what they’re NOT telling you.</h3>
                                    <p>
                                      Learn the 4 biggest diabetes myths and 10 critical scientific breakthroughs in the
                                      FREE <i>Tackling Type 2 Masterclass</i>
                                    </p>
                                    <ModalTrigger />
                                    <h5 className="section-heading text-center">
                                      Join world-renowned anti-diabetes expert Brenda Davis, RD, to discover the truth —
                                      and how to enjoy balanced blood sugar, peace of mind, and optimal health. With
                                      food!
                                    </h5>
                                  </>
                                ),
                                v1: (
                                  <>
                                    <p>FREE Masterclass Now Playing For A Limited Time!</p>
                                    <h2 className="section-heading mb-2">Defying Diabetes:</h2>
                                    <h4 className="mt-0">Conventional wisdom says diabetics need to cut out sugar.</h4>
                                    <h5 className="mt-0">Find out what they’re NOT telling you.</h5>
                                    <ModalTrigger />
                                    <h5 className="section-heading text-center">
                                      Join world-renowned anti-diabetes expert Brenda Davis, RD, to discover the truth —
                                      and how to enjoy balanced blood sugar, peace of mind, and optimal health. With
                                      food!
                                    </h5>
                                  </>
                                ),
                                v2: (
                                  <>
                                    <p>FREE Masterclass Now Playing For A Limited Time!</p>
                                    <h2 className="section-heading mb-2">Defying Diabetes:</h2>
                                    <h3 className="text-400 mt-0">
                                      Science-Backed Breakthroughs For <b>Prevention</b> and <b>Reversal</b>
                                    </h3>
                                    <ModalTrigger />
                                    <h5 className="section-heading text-center">
                                      Join world-renowned anti-diabetes expert Brenda Davis, RD, to discover the truth —
                                      and how to enjoy balanced blood sugar, peace of mind, and optimal health. With
                                      food!
                                    </h5>
                                  </>
                                )
                              }[headlineControl]
                            }
                            <div className="d-lg-none">
                              <Row className="d-flex justify-content-center my-4">
                                <Col xs="8" sm="7">
                                  <img
                                    className="img-fluid border-solid border-white border-5 rounded-circle"
                                    src={require('static/backgrounds/663925408-square.jpg')}
                                  />
                                </Col>
                              </Row>
                            </div>
                          </>
                        ) : (
                          <>
                            {defaultSchedule.phase === 'during' ? (
                              <LandingTimer className="countdown-small mb-4" light />
                            ) : (
                              <p>FREE Masterclass Now Playing For A Limited Time!</p>
                            )}
                            <h4 className="section-heading mb-3">
                              <>
                                Conventional Wisdom Says That Type 2 Diabetes is a Progressive and Irreversible Disease.
                              </>
                            </h4>
                            <h3 className="mt-0 mb-3">Here’s what they’re NOT telling you.</h3>
                            <h5 className="section-heading text-center">
                              Join world-renowned anti-diabetes expert Brenda Davis, RD, to discover the truth — and how
                              to enjoy balanced blood sugar, peace of mind, and optimal health. With food!
                            </h5>
                            <p>
                              Learn the 4 biggest diabetes myths and 10 critical scientific breakthroughs in the FREE{' '}
                              <i>Tackling Type 2 Masterclass</i>
                            </p>
                            <div className="d-lg-none">
                              <Row className="d-flex justify-content-center my-4">
                                <Col xs="8" sm="7">
                                  <img
                                    className="img-fluid border-solid border-white border-5 rounded-circle"
                                    src={require('static/backgrounds/663925408-square.jpg')}
                                  />
                                </Col>
                              </Row>
                            </div>
                            <ModalTrigger />
                          </>
                        )}
                      </Col>
                    </Row>
                  </Container>
                </Section>

                <Section id="landing-video" color="light-gray" innerClass="text-center" angle="both">
                  <Container>
                    <Row className="justify-content-center">
                      <Col lg="10" className="video-col px-0 px-sm-3">
                        <div className="px-3 px-sm-0 mb-3">
                          <p className="mb-0">
                            <FontAwesomeIcon icon={faArrowDown} /> Watch the Trailer{' '}
                            <FontAwesomeIcon icon={faArrowDown} />
                          </p>
                        </div>
                        <div>
                          <Video
                            url={videoUrl}
                            label="TT2 - Masterclass Trailer"
                            thumbnail={videoThumbUrl}
                            noButton={videoButton}
                          />
                        </div>
                      </Col>
                    </Row>
                  </Container>
                </Section>

                <Section id="landing-steps" color="white" angle="bottom">
                  <Container>
                    <Row className="d-flex align-items-stretch justify-content-center text-center">
                      <Col xs="12" lg="4" className="d-flex flex-column mb-5 mb-lg-0">
                        <div className="icon">
                          <FontAwesomeIcon className="text-large text-green" icon={faCalendarCheck} />
                        </div>
                        <h5>Add to Your Calendar</h5>
                        <p className="my-auto">
                          Clear your schedule, and grab a notepad. We’re going in-depth to cover the biggest myths and
                          breakthroughs.
                        </p>
                      </Col>
                      <Col xs="12" lg="4" className="d-flex flex-column mb-5 mb-lg-0">
                        <div className="icon">
                          <FontAwesomeIcon className="text-large text-green" icon={faClock} />
                        </div>
                        <h5>90 Minutes</h5>
                        <p className="my-auto">Schedule approximately 90 minutes of quiet time for this Masterclass.</p>
                      </Col>
                      <Col xs="12" lg="4" className="d-flex flex-column">
                        <div className="icon">
                          <FontAwesomeIcon className="text-large text-green" icon={faShareSquare} />
                        </div>
                        <h5>Share This Masterclass</h5>
                        <p className="my-auto">
                          Help loved ones stay healthy. Share this Masterclass with friends and family.
                        </p>
                      </Col>
                    </Row>
                  </Container>
                </Section>

                <Section id="landing-masterclass-info" color="green">
                  <Container className="py-5">
                    <Row className="m-0">
                      <Col xs="12" className="p-0">
                        <Container className="background-white box-shadow-black-25 rounded p-4 py-lg-5" fluid>
                          <Row className="my-3">
                            <Col xs="12">
                              <h5 className="section-heading text-400 text-center">
                                {variant === 'ad' ? (
                                  <>We’re not going to give the same old advice to stop eating candy bars…</>
                                ) : (
                                  <>
                                    We’re not going to give you the same old advice you’ve already heard to stop eating
                                    candy bars…
                                  </>
                                )}
                              </h5>
                              <h3 className="text-center mt-0 mb-3">
                                {variant === 'ad' ? (
                                  <>What We’ll Cover In This Masterclass:</>
                                ) : (
                                  <>In This Masterclass You Will:</>
                                )}
                              </h3>
                              <Row className="d-flex justify-content-center">
                                <Col xs="12" lg="11">
                                  <IconList className="mb-0">
                                    <IconListItem>
                                      <h5 className="section-heading mb-1">
                                        {variant === 'ad' ? (
                                          <>
                                            Get the latest updates about what <i>NEVER</i> to eat.
                                          </>
                                        ) : (
                                          <>
                                            Discover what <i>NEVER</i> to eat if you want to prevent diabetes and
                                            prediabetes.
                                          </>
                                        )}
                                      </h5>
                                      <p>
                                        {variant === 'ad' ? (
                                          <>(And why carbs aren’t as bad as they’re made out to be.)</>
                                        ) : (
                                          <>(And why carbs aren’t the villain they’re made out to be.)</>
                                        )}
                                      </p>
                                    </IconListItem>
                                    <IconListItem>
                                      <h5 className="section-heading mb-1">
                                        {variant === 'ad' ? (
                                          <>
                                            Find out why the conventional approach can cause the disease to get WORSE —
                                          </>
                                        ) : (
                                          <>
                                            Find out why the conventional approach to diabetes can cause it to get WORSE
                                            —
                                          </>
                                        )}
                                      </h5>
                                      <p>
                                        {variant === 'ad' ? (
                                          <>and lead to weight gain and added complications.</>
                                        ) : (
                                          <>
                                            and how to fix the real ROOT cause. (Thousands have gotten out of the danger
                                            zone this way.)
                                          </>
                                        )}
                                      </p>
                                    </IconListItem>
                                    <IconListItem>
                                      <h5 className="section-heading mb-1">
                                        {variant === 'ad' ? (
                                          <>
                                            Learn a simple reframe to lose weight and slash risk by eating <i>more</i>{' '}
                                            food.
                                          </>
                                        ) : (
                                          <>
                                            Learn a simple reframe to lose weight and slash diabetes risk by eating{' '}
                                            <i>more</i> food.
                                          </>
                                        )}
                                      </h5>
                                      <p>
                                        {variant === 'ad' ? (
                                          <>This simple change can reset metabolism permanently.</>
                                        ) : (
                                          <>This simple switch can reset your metabolism to burn fat while you sleep.</>
                                        )}
                                      </p>
                                    </IconListItem>
                                    <IconListItem>
                                      <h5 className="section-heading mb-1">
                                        {variant === 'ad' ? (
                                          <>
                                            Debunk the widely-held myth about getting enough protein that can cause an
                                            INCREASE in risk.
                                          </>
                                        ) : (
                                          <>
                                            Debunk the widely-held myth about eating enough protein that can cause an
                                            INCREASE in diabetes risk.
                                          </>
                                        )}
                                      </h5>
                                    </IconListItem>
                                    <IconListItem>
                                      <h5 className="section-heading mb-1">
                                        Discover the single most important thing to do after a meal to prevent diabetes.
                                      </h5>
                                      <p>
                                        {variant === 'ad' ? (
                                          <>(This works, especially if it’s been hard to exercise lately.)</>
                                        ) : (
                                          <>(This works, especially if you’ve had a hard time exercising lately.)</>
                                        )}
                                      </p>
                                    </IconListItem>
                                    <IconListItem>
                                      <h5 className="section-heading mb-1">
                                        Demystify terms like blood sugar and insulin resistance,
                                      </h5>
                                      <p>
                                        and get straight talk that’s easy to understand. (Most doctors don’t have time
                                        to explain this.)
                                      </p>
                                    </IconListItem>
                                    <IconListItem>
                                      <h5 className="section-heading mb-1">
                                        {variant === 'ad' ? (
                                          <>Understand why no one can afford to ignore diabetes:</>
                                        ) : (
                                          <>
                                            Understand why you can’t afford to ignore diabetes — even if you haven’t
                                            been diagnosed yet:
                                          </>
                                        )}
                                      </h5>
                                      <p>
                                        {variant === 'ad' ? (
                                          <>
                                            Stop surrendering to a life riddled with fatigue, extra weight (especially
                                            around the belly), and failed attempts at eating differently. These simple
                                            steps will change EVERYTHING.
                                          </>
                                        ) : (
                                          <>
                                            Stop surrendering to a life riddled with pain, fatigue, extra weight
                                            (including the most dangerous kind around the belly), and failed attempts at
                                            changing your diet — while slashing your risk of complications like heart
                                            disease, stroke, blindness, amputation, and early death.
                                          </>
                                        )}
                                      </p>
                                    </IconListItem>
                                  </IconList>
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                        </Container>
                      </Col>
                    </Row>
                  </Container>
                </Section>

                <SectionImage
                  id="landing-host-brenda"
                  color="black"
                  imgUrl={require('static/speaker/brenda-headshot.jpg')}
                  imgPosition="left"
                  className="text-white"
                >
                  <h3 className="section-heading">Brenda Davis, RD</h3>
                  <p>
                    Brenda Davis, RD, is a registered dietitian, a respected researcher, and an internationally
                    acclaimed speaker. She is the author/co-author of 12 books with nearly a million copies in print and
                    in 15 languages.
                  </p>
                  <p>
                    Her most recent works include <i>Kick Diabetes Essentials</i> and <i>The Kick Diabetes Cookbook</i>{' '}
                    (with Vesanto Melina). Brenda is also co-author of numerous peer-reviewed journal articles and the
                    lead dietitian for the Diabetes Wellness Project in the Marshall Islands.
                  </p>
                </SectionImage>

                <SectionImage
                  id="landing-host-ocean"
                  color="black"
                  imgUrl="https://cdn.foodrevolution.org/global/team/frn-guest_expert-portrait-square-ocean_robbins.jpg"
                  imgPosition="right"
                  className="text-white"
                >
                  <h3 className="section-heading">Ocean Robbins</h3>
                  <p>
                    Ocean Robbins, co-founder and CEO of the million-member Food Revolution Network, is a TEDx speaker,
                    best-selling author, husband, father, gardener, and dancer. Born in a log cabin and raised on
                    home-grown food, his work is rooted in his family’s health-conscious legacy. From founding the
                    nonprofit YES! at age 16 to leading a global food revolution movement, Ocean is on a mission to
                    transform our food system, drive down the chronic disease epidemic, and mobilize people worldwide to
                    be part of the solution for a healthier planet and population. His books include{' '}
                    <i>31-Day Food Revolution</i> and <i>Powered By Plants.</i>
                  </p>
                </SectionImage>

                <SectionImage
                  id="landing-cta"
                  className="landing-final-cta text-center text-white"
                  color="purple"
                  imgPosition="left"
                  imgUrl={require('static/backgrounds/1357522236.jpg')}
                  angle="none"
                >
                  <h2 className="section-heading">
                    {variant === 'ad' ? (
                      <>Health is worth protecting.</>
                    ) : (
                      <>Your health is worth protecting. Take action by joining the Masterclass today.</>
                    )}
                  </h2>
                  {variant === 'ad' ? null : (
                    <>{defaultSchedule.phase === 'during' ? <LandingTimer className="mt-4" light /> : null}</>
                  )}
                  <ModalTrigger className="my-4" />
                  <p className="balance-text">This Masterclass is absolutely FREE and plays in your local time.</p>
                </SectionImage>
              </>
            ),
            v1: (
              <>
                <Header bgColor="white" adLockup={variant === 'ad' ? true : false} />
                <SectionTriangle
                  id="landing-header-v1"
                  imgUrl="https://cdn.foodrevolution.org/tt2/images/1253414265.jpg"
                  color="black"
                  innerClass="p-0 my-auto"
                  className={classNames(`d-flex`, `flex-column`, `background-fixed`)}
                >
                  <Container>
                    <Row>
                      <Col>
                        <div className="background-white rounded box-shadow-black-50 p-4 p-lg-5 optin-box text-center">
                          <p>
                            <b>
                              <i>Now Playing For A Limited Time!</i>
                            </b>
                          </p>
                          <Logo className="mb-4" />
                          <h2 className="section-heading mb-3">
                            Conventional Wisdom Says That Type 2 Diabetes is a Progressive and Irreversible Disease.
                          </h2>
                          <h3 className="mt-0 mb-3">Here’s what they’re NOT telling you.</h3>
                          <h5 className="section-heading text-400 text-center mb-4">
                            Join world-renowned anti-diabetes expert Brenda Davis, RD, to discover the truth — and how
                            to enjoy balanced blood sugar, peace of mind, and optimal health. With food!
                          </h5>
                          <ModalTrigger />
                        </div>
                      </Col>
                    </Row>
                  </Container>
                </SectionTriangle>
                <SectionTriangle
                  id="landing-video-v1"
                  color="white"
                  className={classNames(`text-center`, `section-triangle-offset-top-none`)}
                  type="both"
                >
                  <Container>
                    <Row className="justify-content-center">
                      <Col lg="10" className="video-col px-0 px-sm-3">
                        <Video url={videoUrl} thumbnail={videoThumbUrl} noButton />
                      </Col>
                    </Row>
                  </Container>
                </SectionTriangle>
                <SectionTriangle id="" color="purple" type="bottom" className="text-center text-white">
                  <Container>
                    <Row>
                      <Col>
                        <h3 className="section-only-heading text-400">
                          In This <b>90-Minute Masterclass,</b> We’ll Reveal:
                        </h3>
                      </Col>
                    </Row>
                  </Container>
                </SectionTriangle>
                <SectionTriangle id="" color="white" type="bottom">
                  <Container>
                    <Row>
                      <Col>
                        <ListIcon iconColor="purple" className="m-0">
                          <>
                            <h5 className="section-heading mb-1 text-purple">
                              {variant === 'ad' ? (
                                <>
                                  Get the latest updates about what <i>NEVER</i> to eat.
                                </>
                              ) : (
                                <>
                                  Discover what <i>NEVER</i> to eat if you want to prevent diabetes and prediabetes.
                                </>
                              )}
                            </h5>
                            <p>
                              {variant === 'ad' ? (
                                <>(And why carbs aren’t as bad as they’re made out to be.)</>
                              ) : (
                                <>(And why carbs aren’t the villain they’re made out to be.)</>
                              )}
                            </p>
                          </>
                          <>
                            <h5 className="section-heading mb-1 text-purple">
                              {variant === 'ad' ? (
                                <>Find out why the conventional approach can cause the disease to get WORSE —</>
                              ) : (
                                <>Find out why the conventional approach to diabetes can cause it to get WORSE —</>
                              )}
                            </h5>
                            <p>
                              {variant === 'ad' ? (
                                <>and lead to weight gain and added complications.</>
                              ) : (
                                <>
                                  and how to fix the real ROOT cause. (Thousands have gotten out of the danger zone this
                                  way.)
                                </>
                              )}
                            </p>
                          </>
                          <>
                            <h5 className="section-heading mb-1 text-purple">
                              {variant === 'ad' ? (
                                <>
                                  Learn a simple reframe to lose weight and slash risk by eating <i>more</i> food.
                                </>
                              ) : (
                                <>
                                  Learn a simple reframe to lose weight and slash diabetes risk by eating <i>more</i>{' '}
                                  food.
                                </>
                              )}
                            </h5>
                            <p>
                              {variant === 'ad' ? (
                                <>This simple change can reset metabolism permanently.</>
                              ) : (
                                <>This simple switch can reset your metabolism to burn fat while you sleep.</>
                              )}
                            </p>
                          </>
                          <>
                            <h5 className="section-heading mb-1 text-purple">
                              {variant === 'ad' ? (
                                <>Debunk the widely-held myth about getting enough protein</>
                              ) : (
                                <>Debunk the widely-held myth about eating enough protein</>
                              )}
                            </h5>
                            <p>
                              {variant === 'ad' ? (
                                <>that can cause an INCREASE in risk.</>
                              ) : (
                                <>that can cause an INCREASE in diabetes risk.</>
                              )}
                            </p>
                          </>
                          <>
                            <h5 className="section-heading mb-1 text-purple">
                              Discover the single most important thing to do after a meal to prevent diabetes.
                            </h5>
                            <p>
                              {variant === 'ad' ? (
                                <>(This works, especially if it’s been hard to exercise lately.)</>
                              ) : (
                                <>(This works, especially if you’ve had a hard time exercising lately.)</>
                              )}
                            </p>
                          </>
                          <>
                            <h5 className="section-heading mb-1 text-purple">
                              Demystify terms like blood sugar and insulin resistance,
                            </h5>
                            <p>
                              and get straight talk that’s easy to understand. (Most doctors don’t have time to explain
                              this.)
                            </p>
                          </>
                          <>
                            <h5 className="section-heading mb-1 text-purple">
                              {variant === 'ad' ? (
                                <>Understand why no one can afford to ignore diabetes:</>
                              ) : (
                                <>
                                  Understand why you can’t afford to ignore diabetes — even if you haven’t been
                                  diagnosed yet:
                                </>
                              )}
                            </h5>
                            <p>
                              {variant === 'ad' ? (
                                <>
                                  Stop surrendering to a life riddled with fatigue, extra weight (especially around the
                                  belly), and failed attempts at eating differently. These simple steps will change
                                  EVERYTHING.
                                </>
                              ) : (
                                <>
                                  Stop surrendering to a life riddled with pain, fatigue, extra weight (including the
                                  most dangerous kind around the belly), and failed attempts at changing your diet —
                                  while slashing your risk of complications like heart disease, stroke, blindness,
                                  amputation, and early death.
                                </>
                              )}
                            </p>
                          </>
                        </ListIcon>
                      </Col>
                    </Row>
                  </Container>
                </SectionTriangle>
                <SectionTriangle id="" color="purple" type="bottom" className="text-center text-white">
                  <Container>
                    <Row>
                      <Col>
                        <h3 className="section-only-heading">Meet Your Instructor</h3>
                      </Col>
                    </Row>
                  </Container>
                </SectionTriangle>
                <SectionTriangleSpeakerCollapse
                  id=""
                  // @ts-ignore
                  imgUrl={require('static/speaker/brenda-headshot.jpg')}
                  imgPosition="right"
                  color="white"
                  type="bottom"
                  headlines={
                    <>
                      <h2 className="section-heading mb-2">Brenda Davis, RD</h2>
                      <h4 className="m-0 text-400">
                        Brenda Davis, RD, is a registered dietitian, a respected researcher, and an internationally
                        acclaimed speaker.
                      </h4>
                    </>
                  }
                  collapseHeader={
                    <>
                      <h5 className="text-400 m-0">Click Here to</h5>
                      <h4 className="m-0">Learn More About Brenda Davis, RD</h4>
                    </>
                  }
                  collapseHeaderBgColor="purple"
                  content={
                    <>
                      <p>
                        Brenda Davis, RD, is a registered dietitian, a respected researcher, and an internationally
                        acclaimed speaker. She is the author/co-author of 12 books with nearly a million copies in print
                        and in 15 languages.
                      </p>
                      <p>
                        Her most recent works include <i>Kick Diabetes Essentials</i> and{' '}
                        <i>The Kick Diabetes Cookbook</i> (with Vesanto Melina). Brenda is also co-author of numerous
                        peer-reviewed journal articles and the lead dietitian for the Diabetes Wellness Project in the
                        Marshall Islands.
                      </p>
                    </>
                  }
                />
                <SectionTriangle id="" color="light-gray" type="bottom" innerClass="mobile-padding">
                  <Container>
                    <Row>
                      <Col>
                        <h4 className="section-heading text-center mb-5">
                          Here’s what some of the world’s leading figures have said about Brenda Davis, MD
                        </h4>
                        <TestimonialBubble id="tt2-michael-greger" bgColor="white" className="mb-5" />
                        <TestimonialBubble id="tt2-neal-barnard" bgColor="white" className="mb-5" />
                        <TestimonialBubble id="tt2-scott-stoll" bgColor="white" className="mb-5" />
                        <TestimonialBubble id="tt2-michael-klaper" bgColor="white" className="mb-5" />
                        <TestimonialBubble id="tt2-julieanna-hever" bgColor="white" />
                      </Col>
                    </Row>
                  </Container>
                </SectionTriangle>
                <SectionTriangle id="" color="purple" type="bottom" className="text-center text-white">
                  <Container>
                    <Row>
                      <Col>
                        <h3 className="section-only-heading">Meet Your Host</h3>
                      </Col>
                    </Row>
                  </Container>
                </SectionTriangle>
                <SectionTriangleSpeakerCollapse
                  id=""
                  imgUrl="https://cdn.foodrevolution.org/global/team/frn-guest_expert-portrait-square-ocean_robbins.jpg"
                  imgPosition="left"
                  color="white"
                  type="bottom"
                  headlines={
                    <>
                      <h2 className="section-heading mb-2">Ocean Robbins</h2>
                      <h4 className="m-0 text-400">A food revolution leader and inspiration to millions.</h4>
                    </>
                  }
                  collapseHeader={
                    <>
                      <h5 className="text-400 m-0">Click Here to</h5>
                      <h4 className="m-0">Learn More About Ocean Robbins</h4>
                    </>
                  }
                  collapseHeaderBgColor="purple"
                  content={
                    <>
                      <p>
                        Ocean Robbins, co-founder and CEO of the million-member Food Revolution Network, is a TEDx
                        speaker, best-selling author, husband, father, gardener, and dancer. Born in a log cabin and
                        raised on home-grown food, his work is rooted in his family’s health-conscious legacy. From
                        founding the nonprofit YES! at age 16 to leading a global food revolution movement, Ocean is on
                        a mission to transform our food system, drive down the chronic disease epidemic, and mobilize
                        people worldwide to be part of the solution for a healthier planet and population. His books
                        include <i>31-Day Food Revolution</i> and <i>Powered By Plants.</i>
                      </p>
                    </>
                  }
                />
                <SectionTriangle id="" color="light-gray" type="none" innerClass="text-center">
                  <Container>
                    <Row>
                      <Col>
                        <div className="background-white p-4 p-lg-5 rounded box-shadow-black-50">
                          <h2 className="section-heading text-purple mb-2">
                            <i>Don’t Miss Your Chance!</i>
                          </h2>
                          <h3 className="m-0">For a limited time watch the</h3>
                          <Logo className="my-4" />
                          <h4 className="text-purple my-4">
                            <i>It’s Completely Free.</i>
                          </h4>
                          <div>
                            <ModalTrigger className="mb-0" />
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Container>
                </SectionTriangle>
              </>
            )
          }[designControl]
        }
      </div>

      <div className="footer-content mt-auto">
        <Footer adVariant={variant === 'ad' ? true : false} />
        <ShareButtons floating />
      </div>

      <OptinModal variant={variant} />
      {noExitIntent ? null : <ModalExitLanding />}
    </div>
  );
}
